import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { apiFetch } from "../apiFetch";

/**
 * Sentinel-1 radar coherence at one pylon (#118).
 *
 * States the measurement and stops. There is no coloured badge and no verdict,
 * for a specific reason: the threshold behind a drop has never been validated
 * against a confirmed ground movement, because there has not been one. #103 is the
 * precedent for what happens when a threshold picked in advance drives a colour:
 * the corridor NDVI badge sat amber for a third of every year and meant nothing.
 *
 * So a drop is reported in words, with the numbers behind it, and the reader
 * decides. Once a reading has been confirmed against something that happened on
 * the ground, escalating it to a colour is a small change made against evidence.
 *
 * It is no longer one threshold for every node (#194). The bar is the larger of
 * 0.25 and three times that node's own median absolute deviation, because a single
 * absolute fall measures how high a node sits rather than how far it moved: a node
 * on bare rock at 0.90 has 0.90 of room to fall and a node in scrub at 0.49 does
 * not.
 *
 * And a fall is not always a ground change. On 2026-10-12 this panel reported a
 * drop at node-002, a crew went out, and nothing was wrong: both radar
 * acquisitions had fallen on days with a RED rain alert, the ground there is rock
 * with no soil, and standing water throws the radar away from the satellite until
 * it dries. The next clear pair read 0.872 against a median of 0.895. That case
 * now arrives as `weather_suspect` and says what it is, rather than sending
 * somebody to look at rain.
 *
 * WHAT THE NUMBER IS
 *
 * Coherence, 0 to 1, says how correlated the radar return from the ground around
 * this pylon was between two Sentinel-1 passes six days apart. It is not
 * displacement and cannot say a tower moved by any distance — that needs InSAR
 * phase analysis over a stack of 50+ pairs.
 *
 * It is the ground, not the tower. This comment used to say the tower dominates
 * its pixel, which would have made the reading a statement about the structure;
 * measured on 2026-09-01, it does not. One cell is about 400 m2 in the weekly
 * product and 294 m2 even at full resolution with multilooking off, against a
 * lattice tower of a few tens of square metres, and no node showed a point-target
 * signature. Per-pylon structural monitoring is not available from Sentinel-1 —
 * see the closing comment on PR #126.
 *
 * That is why the strings this renders say "ground stability" and "this says
 * nothing about the tower", and why the comparison that means something is a node
 * against its own past, never against another node: a lower reading at one tower
 * usually means it stands in a worked field and another does not.
 */
export default function NodeGroundStability({ nodeId, apiBaseUrl, t, simulated }) {
  const [reading, setReading] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!nodeId || simulated) {
      setReading(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setReading(null);
    setError(null);

    apiFetch(`${apiBaseUrl}/api/coherence?node_id=${encodeURIComponent(nodeId)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`API error ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setReading(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [nodeId, apiBaseUrl, simulated]);

  // Below superuser no request is issued, matching every other /api/ consumer.
  if (simulated) return null;

  const line = (text, muted = true) => (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t("groundStability")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: muted ? "text.secondary" : "#7f1d1d", mt: 0.25 }}
      >
        {text}
      </Typography>
    </Box>
  );

  if (error) return line(t("groundStabilityError"));
  if (!reading) return line(t("groundStabilityLoading"));

  // Never measured, table absent, or the route could not answer. All of them mean
  // "we have not looked", which is a different statement from a stable pylon and
  // must not be shown as one.
  if (reading.status === "unavailable" || reading.coherence === null) {
    return line(
      reading.status === "not_measured"
        ? t("groundStabilityNotMeasured")
        : t("groundStabilityUnavailable")
    );
  }

  const value = Number(reading.coherence).toFixed(2);
  const window =
    reading.pair_start && reading.pair_end
      ? `${reading.pair_start} → ${reading.pair_end}`
      : t("groundStabilityDatesUnknown");

  const stale = reading.stale
    ? t("groundStabilityStale", { n: reading.check_age_days })
    : "";

  // Weather over the two acquisition dates (#194). Only appended when the lookup
  // actually succeeded: `weather_correlated` is null when it could not be checked,
  // and saying nothing is better than implying it was calm. The summary itself is
  // the English string lambda_weather.py wrote, in both languages — translating a
  // stored alert name after the fact would be guessing at it.
  const weather =
    reading.weather_correlated === true && reading.weather_summary
      ? t("groundStabilityWeather", { weather: reading.weather_summary })
      : "";

  const numbers = {
    value,
    drop: Math.abs(Number(reading.drop_vs_median)).toFixed(2),
    median: Number(reading.median_of_last_n).toFixed(2),
    window,
  };

  // The fall passed this node's own bar, but a severe precipitation alert covered
  // an acquisition date, so the pair cannot separate wet ground from moved ground.
  // Not styled as an alert — the whole point is that it is not one yet — and not
  // hidden either, because the measurement happened.
  if (reading.status === "weather_suspect" && reading.drop_vs_median !== null) {
    return line(
      t("groundStabilitySuspect", {
        ...numbers,
        weather: reading.weather_summary || t("groundStabilityWeatherUnnamed"),
      }) + stale
    );
  }

  // A drop is stated in words with its own numbers attached, not coloured. The
  // threshold behind it has never been validated against a confirmed event.
  if (reading.status === "drop" && reading.drop_vs_median !== null) {
    return line(t("groundStabilityDrop", numbers) + weather + stale, false);
  }

  if (reading.status === "no_baseline") {
    return line(t("groundStabilityFirst", { value, window }) + stale);
  }

  return line(t("groundStabilityOk", { value, window }) + stale);
}
